import { useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useThemeContext } from "../globalContext/global-context";
import type { ThemeV2 } from "../util/types";
import type { QuestionHost } from "../questions/hosts/shared/types";
import { formsDropdownSlotProps } from "./formsDropdown";

export const createSurveyMuiTheme = (
  surveyTheme: ThemeV2,
  host: QuestionHost = "chat",
) => {
  const { color, typography, shape, spacing } = surveyTheme.tokens;
  const spacingValues = [
    0,
    spacing["1"],
    spacing["2"],
    spacing["3"],
    spacing["4"],
    spacing["5"],
    spacing["6"],
  ];
  const space = (factor: number | string) => {
    if (typeof factor === "string") return factor;
    const magnitude = Math.abs(factor);
    const lower = Math.min(Math.floor(magnitude), 6);
    const upper = Math.min(Math.ceil(magnitude), 6);
    const value =
      magnitude > 6
        ? spacingValues[6] +
          (magnitude - 6) * (spacingValues[6] - spacingValues[5])
        : spacingValues[lower] +
          (spacingValues[upper] - spacingValues[lower]) * (magnitude - lower);
    return `${Math.sign(factor) * value}px`;
  };
  return createTheme({
    components: {
      MuiAutocomplete: {
        defaultProps: {
          openOnFocus: host === "chat",
          ...(host === "forms"
            ? { slotProps: formsDropdownSlotProps(spacing["2"]) }
            : {}),
        },
      },
    },
    spacing: (...factors: (number | string)[]) => factors.map(space).join(" "),
    palette: {
      mode: "light",
      primary: {
        main: color.action.primary,
        contrastText: color.action.primaryText,
      },
      secondary: {
        main: color.action.primary,
        contrastText: color.action.primaryText,
      },
      error: { main: color.feedback.error },
      text: { primary: color.text.primary, secondary: color.text.secondary },
      background: { default: color.surface.canvas, paper: color.surface.card },
      divider: color.border.default,
    },
    typography: {
      fontFamily: typography.fontFamily,
      fontWeightRegular: typography.weight.regular,
      fontWeightMedium: typography.weight.medium,
      fontWeightBold: typography.weight.bold,
      body1: {
        fontSize: `${typography.size.md}px`,
        lineHeight: typography.lineHeight.normal,
      },
      body2: {
        fontSize: `${typography.size.sm}px`,
        lineHeight: typography.lineHeight.normal,
      },
      button: {
        fontSize: `${typography.size.md}px`,
        fontWeight: typography.weight.medium,
        lineHeight: typography.lineHeight.normal,
        textTransform: "none",
      },
      caption: {
        fontSize: `${typography.size.xs}px`,
        lineHeight: typography.lineHeight.normal,
      },
      subtitle1: {
        fontSize: `${typography.size.lg}px`,
        fontWeight: typography.weight.medium,
        lineHeight: typography.lineHeight.normal,
      },
      subtitle2: {
        fontSize: `${typography.size.sm}px`,
        fontWeight: typography.weight.medium,
        lineHeight: typography.lineHeight.normal,
      },
    },
    shape: { borderRadius: shape.radius.md },
  });
};

export const SurveyHostThemeProvider = ({
  children,
  host = "chat",
}: {
  children: React.ReactNode;
  host?: QuestionHost;
}) => {
  const { globalObject } = useThemeContext();
  const surveyTheme = globalObject.themeData.normalizedTheme;
  const muiTheme = useMemo(
    () => createSurveyMuiTheme(surveyTheme, host),
    [surveyTheme, host],
  );
  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>;
};
